import { type RouteObject } from 'react-router-dom';

// import { ProviderRegistrationAuth } from '@/app/ProviderRegistrationAuth';
import RouteGuard from '@/registration/component/Layout/RouteGuard';
import GroupRouteGuard from '@/registration/component/Layout/GroupRouteGuard';

import RegistrationEmailPage from '@/registration/register/Email';
import RegistrationRegisterPersonalInfoPage from '@/registration/register/PersonalInfo';
import RegistrationRegisterCategoryPage from '@/registration/register/Category';
import RegistrationRegisterMainTicketsPage from '@/registration/register/MainTickets';
import RegistrationRegisterFunctionTicketsPage from '@/registration/register/FunctionTickets';
import RegistrationRegisterWorkshopTicketsPage from '@/registration/register/WorkshopTickets';
import RegistrationRegisterTourTicketsPage from '@/registration/register/TourTickets';
import RegistrationRegisterShoppingCartPage from '@/registration/register/ShoppingCart';
import RegistrationRegisterAdditionalInfoPage from '@/registration/register/AdditionalInfo';
import RegistrationRegisterReviewPage from '@/registration/register/Review';
import RegistrationRegisterCheckoutAddressPage from '@/registration/register/CheckoutAddress';
import RegistrationRegisterPaymentPage from '@/registration/register/Payment';
import RegistrationRegisterPaymentProcessingPage from '@/registration/register/PaymentProcessing';
import RegistrationRegisterConfirmationPage from '@/registration/register/Confirmation';
import RegistrationRegisterLandingPage from '@/registration/register/LandingPage';

import RegistrationRegisterGroupCategoryPage from '@/registration/register/GroupCategory';
import RegistrationRegisterGroupAttendeesPage from 'src/registration/register/GroupAttendees';
import RegistrationRegisterGroupMainTicketsPage from '@/registration/register/GroupMainTickets';
import RegistrationRegisterGroupFunctionTicketsPage from '@/registration/register/GroupFunctionTickets';
import RegistrationRegisterGroupWorkshopTicketsPage from '@/registration/register/GroupWorkshopTickets';
import RegistrationRegisterGroupTourTicketsPage from '@/registration/register/GroupTourTickets';
import RegistrationRegisterGroupShoppingCartPage from '@/registration/register/GroupShoppingCart';
import RegistrationRegisterGroupCheckoutAddressPage from '@/registration/register/GroupCheckoutAddress';
import RegistrationRegisterGroupPaymentPage from '@/registration/register/GroupPayment';
import RegistrationRegisterGroupPaymentProcessingPage from '@/registration/register/GroupPaymentProcessing';
import RegistrationRegisterGroupConfirmationPage from '@/registration/register/GroupConfirmation';
import RegistrationRegisterInvitedConfirmationPage from '@/registration/register/InvitedConfirmation';
import RegistrationRegisterInviteeTermsPrivacy from '@/registration/register/InviteeTermsPrivacy';
import RegistrationRegisterRegistrationDetailsPage from '@/registration/register/RegistrationDetails';
import ModeGuard from './component/Layout/ModeGuard';
import PageVisitTracker from './component/PageVisitTracker';
import RegistrationRegisterOutstandingPaymentPage from '@/registration/register/OutstandingPayment';
import RegistrationAddToCalendarPage from '@/registration/register/AddToCalendar';
import ProviderGTM from '@/app/ProviderGTM';

export interface RegisterRouteParams {
  RegistrationRegisterEmail: { tenantCode: string, eventId: string }
  RegistrationRegisterPersonalInfo: { tenantCode: string, eventId: string }
  RegistrationRegisterCategory: { tenantCode: string, eventId: string }
  RegistrationRegisterMainTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterFunctionTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterWorkshopTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterTourTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterShoppingCart: { tenantCode: string, eventId: string }
  RegistrationRegisterAdditionalInfo: { tenantCode: string, eventId: string }
  RegistrationRegisterReview: { tenantCode: string, eventId: string }
  RegistrationRegisterCheckoutAddress: { tenantCode: string, eventId: string }
  RegistrationRegisterPayment: { tenantCode: string, eventId: string }
  RegistrationRegisterPaymentProcessing: { tenantCode: string, eventId: string }
  RegistrationRegisterConfirmation: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupAttendees: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupCategory: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupMainTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupFunctionTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupWorkshopTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupTourTickets: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupShoppingCart: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupCheckoutAddress: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupPayment: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupPaymentProcessing: { tenantCode: string, eventId: string }
  RegistrationRegisterGroupConfirmation: { tenantCode: string, eventId: string }
  RegistrationRegisterLandingPage: { tenantCode: string, eventId: string }
  RegistrationRegisterInvitedConfirmation: { tenantCode: string, eventId: string }
  RegistrationRegisterInviteeTermsPrivacy: { tenantCode: string, eventId: string }
  RegistrationRegisterRegistrationDetails: { tenantCode: string, eventId: string }
  RegistrationRegisterOutstandingPayment: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterFunctionTickets: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterWorkshopTickets: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterTourTickets: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterShoppingCart: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterReview: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterCheckoutAddress: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterPayment: { tenantCode: string, eventId: string }
  RegistrationUpsellRegisterConfirmation: { tenantCode: string, eventId: string }
  RegistrationAddToCalendar: { tenantCode: string, eventId: string }
}

export interface RegistrationRouteParams extends RegisterRouteParams { }

export type RegistrationRouteName = keyof RegistrationRouteParams;

export const registerRoutes: Record<keyof RegisterRouteParams, RouteObject> = {
  RegistrationRegisterEmail: {
    id: 'RegistrationRegisterEmail',
    path: ':eventId/email',
    element: <ModeGuard>
      <ProviderGTM>
        <RegistrationEmailPage />
      </ProviderGTM>
    </ModeGuard>
  },
  RegistrationRegisterCategory: {
    id: 'RegistrationRegisterCategory',
    path: ':eventId/category',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterCategoryPage />
          <PageVisitTracker pageName='RegistrationRegisterCategory' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterPersonalInfo: {
    id: 'RegistrationRegisterPersonalInfo',
    path: ':eventId/personal-info',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterPersonalInfoPage />
          <PageVisitTracker pageName='RegistrationRegisterPersonalInfo' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterMainTickets: {
    id: 'RegistrationRegisterMainTickets',
    path: ':eventId/main-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterMainTicketsPage />
          <PageVisitTracker pageName='RegistrationRegisterMainTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterFunctionTickets: {
    id: 'RegistrationRegisterFunctionTickets',
    path: ':eventId/function-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterFunctionTicketsPage />
          <PageVisitTracker pageName='RegistrationRegisterFunctionTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterWorkshopTickets: {
    id: 'RegistrationRegisterWorkshopTickets',
    path: ':eventId/workshop-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterWorkshopTicketsPage />
          <PageVisitTracker pageName='RegistrationRegisterWorkshopTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterTourTickets: {
    id: 'RegistrationRegisterTourTickets',
    path: ':eventId/tour-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterTourTicketsPage />
          <PageVisitTracker pageName='RegistrationRegisterTourTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterShoppingCart: {
    id: 'RegistrationRegisterShoppingCart',
    path: ':eventId/shopping-cart',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterShoppingCartPage />
          <PageVisitTracker pageName='RegistrationRegisterShoppingCart' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterAdditionalInfo: {
    id: 'RegistrationRegisterAdditionalInfo',
    path: ':eventId/additional-info',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterAdditionalInfoPage />
          <PageVisitTracker pageName='RegistrationRegisterAdditionalInfo' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterReview: {
    id: 'RegistrationRegisterReview',
    path: ':eventId/review',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterReviewPage />
          <PageVisitTracker pageName='RegistrationRegisterReview' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterCheckoutAddress: {
    id: 'RegistrationRegisterCheckoutAddress',
    path: ':eventId/checkout-address',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterCheckoutAddressPage />
          <PageVisitTracker pageName='RegistrationRegisterCheckoutAddress' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterPayment: {
    id: 'RegistrationRegisterPayment',
    path: ':eventId/payment',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterPaymentPage />
          <PageVisitTracker pageName='RegistrationRegisterPayment' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterPaymentProcessing: {
    id: 'RegistrationRegisterPaymentProcessing',
    path: ':eventId/payment-processing',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterPaymentProcessingPage />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterConfirmation: {
    id: 'RegistrationRegisterConfirmation',
    path: ':eventId/confirmation',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterConfirmationPage />
          <PageVisitTracker pageName='RegistrationRegisterConfirmation' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationRegisterGroupAttendees: {
    id: 'RegistrationRegisterGroupAttendees',
    path: ':eventId/group-attendees',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupAttendeesPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupCategory: {
    id: 'RegistrationRegisterGroupCategory',
    path: ':eventId/group-category',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupCategoryPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupMainTickets: {
    id: 'RegistrationRegisterGroupMainTickets',
    path: ':eventId/group-main-tickets',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupMainTicketsPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupFunctionTickets: {
    id: 'RegistrationRegisterGroupFunctionTickets',
    path: ':eventId/group-function-tickets',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupFunctionTicketsPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupWorkshopTickets: {
    id: 'RegistrationRegisterGroupWorkshopTickets',
    path: ':eventId/group-workshop-tickets',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupWorkshopTicketsPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupTourTickets: {
    id: 'RegistrationRegisterGroupTourTickets',
    path: ':eventId/group-tour-tickets',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupTourTicketsPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupCheckoutAddress: {
    id: 'RegistrationRegisterGroupCheckoutAddress',
    path: ':eventId/group-checkout-address',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupCheckoutAddressPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupShoppingCart: {
    id: 'RegistrationRegisterGroupShoppingCart',
    path: ':eventId/group-shopping-cart',
    element: <ModeGuard>
      <GroupRouteGuard>
        <ProviderGTM>
          <RegistrationRegisterGroupShoppingCartPage />
        </ProviderGTM>
      </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupPayment: {
    id: 'RegistrationRegisterGroupPayment',
    path: ':eventId/group-payment',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupPaymentPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupPaymentProcessing: {
    id: 'RegistrationRegisterGroupPaymentProcessing',
    path: ':eventId/group-payment-processing',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupPaymentProcessingPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterGroupConfirmation: {
    id: 'RegistrationRegisterGroupConfirmation',
    path: ':eventId/group-confirmation',
    element: <ModeGuard><GroupRouteGuard>
      <ProviderGTM>
        <RegistrationRegisterGroupConfirmationPage />
      </ProviderGTM>
    </GroupRouteGuard></ModeGuard>
  },
  RegistrationRegisterLandingPage: {
    id: 'RegistrationRegisterLandingPage',
    path: ':eventId/landing-page',
    element: <ModeGuard skipCloseMode={true}>
      <ProviderGTM>
        <RegistrationRegisterLandingPage />
      </ProviderGTM>
    </ModeGuard>
  },
  RegistrationRegisterInvitedConfirmation: {
    id: 'RegistrationRegisterInvitedConfirmationPage',
    path: ':eventId/invited-confirmation',
    element: <ModeGuard>
      <ProviderGTM>
        <RegistrationRegisterInvitedConfirmationPage />
      </ProviderGTM>
    </ModeGuard>
  },
  RegistrationRegisterInviteeTermsPrivacy: {
    id: 'RegistrationRegisterInviteeTermsPrivacy',
    path: ':eventId/invitee-terms-privacy',
    element: <ModeGuard>
      <ProviderGTM>
        <RegistrationRegisterInviteeTermsPrivacy />
      </ProviderGTM>
    </ModeGuard>
  },
  RegistrationRegisterRegistrationDetails: {
    id: 'RegistrationRegisterRegistrationDetailsPage',
    path: ':eventId/details',
    element: <ModeGuard skipCloseMode={true}>
      <ProviderGTM>
        <RegistrationRegisterRegistrationDetailsPage />
      </ProviderGTM >
    </ModeGuard>
  },
  RegistrationRegisterOutstandingPayment: {
    id: 'RegistrationRegisterOutstandingPayment',
    path: ':eventId/outstanding-payment',
    element: <ModeGuard>
      <ProviderGTM>
        <RegistrationRegisterOutstandingPaymentPage />
      </ProviderGTM>
    </ModeGuard>
  },
  RegistrationUpsellRegisterFunctionTickets: {
    id: 'RegistrationUpsellRegisterFunctionTickets',
    path: ':eventId/upsell/function-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterFunctionTicketsPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterFunctionTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterWorkshopTickets: {
    id: 'RegistrationUpsellRegisterWorkshopTickets',
    path: ':eventId/upsell/workshop-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterWorkshopTicketsPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterWorkshopTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterTourTickets: {
    id: 'RegistrationUpsellRegisterTourTickets',
    path: ':eventId/upsell/tour-tickets',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterTourTicketsPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterTourTickets' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterShoppingCart: {
    id: 'RegistrationUpsellRegisterShoppingCart',
    path: ':eventId/upsell/shopping-cart',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterShoppingCartPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterShoppingCart' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterReview: {
    id: 'RegistrationUpsellRegisterReview',
    path: ':eventId/upsell/review',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterReviewPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterReview' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterCheckoutAddress: {
    id: 'RegistrationUpsellRegisterCheckoutAddress',
    path: ':eventId/upsell/checkout-address',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterCheckoutAddressPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterCheckoutAddress' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterPayment: {
    id: 'RegistrationUpsellRegisterPayment',
    path: ':eventId/upsell/payment',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterPaymentPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterPayment' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationUpsellRegisterConfirmation: {
    id: 'RegistrationUpsellRegisterConfirmation',
    path: ':eventId/upsell/confirmation',
    element: <ModeGuard>
      <RouteGuard>
        <ProviderGTM>
          <RegistrationRegisterConfirmationPage isUpsell={true} />
          <PageVisitTracker pageName='RegistrationUpsellRegisterConfirmation' />
        </ProviderGTM>
      </RouteGuard>
    </ModeGuard>
  },
  RegistrationAddToCalendar: {
    id: 'RegistrationAddToCalendar',
    path: ':eventId/add-to-calendar',
    element: <ModeGuard>
      <ProviderGTM>
        <RegistrationAddToCalendarPage />
      </ProviderGTM>
    </ModeGuard>
  }
};

export const registrationRoutes: Record<string, RouteObject> = {
  // Register routes
  RegisterRoute: {
    path: 'register',
    // element: (<Outlet />),
    children: Object.values(registerRoutes)
  },
};
